import React, { useState } from 'react';
import { Card, Tag, Spin, Modal, Button, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import previewIcon from '../../../assets/images/preview_button_light.png';
import Mobile from '../Mobile/Mobile';
import Advertisement from '../Advertisement/Advertisement';
import ListingView from '../../Dashboard/mobile/ListingView/ListingView';
import ProfileDetails from '../../Dashboard/mobile/ListingView/ProfileDetails';

const ProfileDetailsCards = ({ profileData, loading = false }) => {
  // Preview modal states
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState('listing');

  // Debug logging to help identify data issues and context
  console.log('ProfileDetailsCards - Full profileData:', profileData);
  console.log('ProfileDetailsCards - Context check:', {
    hasOriginalData: !!profileData?.originalData,
    hasFamily: !!profileData?.family,
    hasSiblings: !!(profileData?.family?.siblings || profileData?.siblings),
    hasManglik: !!profileData?.manglik,
    hasHabit: !!profileData?.habit,
    hasHoroscopeImages: !!(profileData?.horoscope_images || profileData?.horoscopeImages)
  });

  const renderValue = (value, fallback = 'Not specified') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      return value.trim() !== '' ? value : fallback;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      // Prefer label, else name/value, else code
      if (value.label && String(value.label).trim() !== '') return value.label;
      if (value.name && String(value.name).trim() !== '') return value.name;
      if (value.value && String(value.value).trim() !== '') return value.value;
      if (value.code !== undefined && value.code !== null) return String(value.code);
    }
    return fallback;
  };

  // Helper function to get value from either API format or display format
  const getFieldValue = (apiField, displayField, fallback = 'Not specified') => {
    // Try API format first (object with label)
    if (apiField && typeof apiField === 'object' && apiField.label) {
      return apiField.label;
    }
    // Try display format (string)
    if (displayField && displayField !== '') {
      return displayField;
    }
    // Try direct string value
    if (typeof apiField === 'string' && apiField !== '') {
      return apiField;
    }
    return fallback;
  };

  // Helper function to get field value with multiple fallback options
  const getFieldValueWithFallbacks = (fieldPaths, fallback = 'Not specified') => {
    for (const path of fieldPaths) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], profileData);
      const rendered = renderValue(value, fallback);
      if (rendered === '') return fallback;
      if (rendered !== fallback) return rendered;
    }
    return fallback;
  };

  // Height formatter that understands multiple shapes: string, object with label/code
  const formatHeightDisplay = (...candidates) => {
    const first = candidates.find(c => c !== undefined && c !== null);
    if (!first) return 'Not specified';

    const raw = typeof first === 'object' ? (first.label || first.value || first.name || first) : first;
    if (typeof raw !== 'string') return renderValue(raw);

    let text = raw.trim();
    if (text.includes('/')) {
      const parts = text.split('/');
      text = parts[parts.length - 1].trim();
    }
    const feetInchesMatch = text.match(/^(\d+)\s*'\s*(\d+)\s*"?$/);
    if (feetInchesMatch) {
      const [, ft, inch] = feetInchesMatch;
      return `${ft}ft ${inch} in`;
    }
    const imperialMatch = text.match(/(\d+)\s*ft\s*(?:([0-9]{1,2})\s*in)?/i);
    if (imperialMatch) {
      const ft = imperialMatch[1];
      const inch = imperialMatch[2];
      return inch !== undefined ? `${ft}ft ${inch} in` : `${ft}ft`;
    }
    // Strip trailing cm if any
    const noCm = text.replace(/\b\d+\s*cm\b.*$/i, '').trim();
    return noCm || text;
  };

  // Function to calculate siblings count from siblings array
  const getSiblingCounts = (p) => {
    try {
      const family = p?.family || {};
      const rawSiblings = family.siblings || p?.siblings || [];
      let siblings = rawSiblings;

      // Handle format where each sibling is an array of { Key, Value }
      const isKeyValueArray = Array.isArray(siblings) && siblings.length > 0 && Array.isArray(siblings[0]);
      if (isKeyValueArray) {
        siblings = siblings.map(kvList => {
          return kvList.reduce((acc, item) => {
            if (item && typeof item === 'object' && 'Key' in item) {
              acc[item.Key] = item.Value;
            }
            return acc;
          }, {});
        });
      }

      if (!Array.isArray(siblings)) siblings = [];

      const isMale = (val) => {
        if (typeof val === 'boolean') return val;
        if (typeof val === 'number') return val === 1;
        if (typeof val === 'string') {
          const v = val.trim().toLowerCase();
          return v === '1' || v === 'true' || v === 'male' || v === 'm';
        }
        return false;
      };

      const brothersFromArray = siblings.filter(s => s && isMale(s.is_male)).length;
      const sistersFromArray = siblings.filter(s => s && !isMale(s.is_male)).length;

      // Prefer explicit sibling objects if present; otherwise use stored counts
      const hasStructuredSiblings = (
        // key/value array format from API
        (Array.isArray(rawSiblings) && rawSiblings.length > 0 && Array.isArray(rawSiblings[0])) ||
        // object format with is_male field
        (Array.isArray(rawSiblings) && rawSiblings.some(s => s && (typeof s.is_male !== 'undefined')))
      ) && (brothersFromArray + sistersFromArray > 0);

      const brothers = hasStructuredSiblings
        ? brothersFromArray
        : (typeof family.brothers_count === 'number' ? family.brothers_count : 0);
      const sisters = hasStructuredSiblings
        ? sistersFromArray
        : (typeof family.sisters_count === 'number' ? family.sisters_count : 0);

      return { brothers, sisters };
    } catch (e) {
      console.error('Error calculating sibling counts:', e);
      return { brothers: 0, sisters: 0 };
    }
  };

  // Calculate siblings count
  const { brothers, sisters } = getSiblingCounts(profileData);

  // Preview functionality
  const showPreviewModal = () => {
    setIsPreviewModalOpen(true);
  };

  const handlePreviewCancel = () => {
    setIsPreviewModalOpen(false);
  };

  // Transform profile data for preview
  const transformProfileDataForPreview = () => {
    if (!profileData) return {};

    // Helper function to safely extract values
    const getValue = (field, fallback = 'Not specified') => {
      if (!field) return fallback;
      if (typeof field === 'string') return field.trim() || fallback;
      if (typeof field === 'object') {
        return field.label || field.name || field.value || fallback;
      }
      return fallback;
    };

    // Helper function to safely extract location values
    const getLocationValue = (locationData, fallback = 'Not specified') => {
      if (!locationData) return fallback;
      
      // Handle different location data structures
      if (typeof locationData === 'string') return locationData.trim() || fallback;
      
      if (typeof locationData === 'object') {
        // Try different possible property names for location data
        return locationData.label || 
               locationData.city?.label || 
               locationData.display_place || 
               locationData.display_name || 
               locationData.name ||
               locationData.value ||
               fallback;
      }
      
      return fallback;
    };

    // Helper function to get age from DOB
    const calculateAge = (dob) => {
      if (!dob) return 'Not specified';
      try {
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      } catch (e) {
        return 'Not specified';
      }
    };

    return {
      name: getValue(profileData.name || profileData.fullName),
      lumi_id: getValue(profileData.lumi_id || profileData.lumiId),
      age: profileData.age || calculateAge(profileData.dob),
      dob: profileData.dob || 'Not specified',
      marital_status: getValue(profileData.marital_status || profileData.maritalStatus),
      religion: getValue(profileData.religion),
      caste: getValue(profileData.caste),
      zodiac_sign: getValue(profileData.birth_star || profileData.birthStar),
      city: getLocationValue(profileData.city || profileData.location),
      state: getLocationValue(profileData.state),
      about_me: getValue(profileData.about_me || profileData.aboutMe || profileData.bio),
      job: getValue(profileData.job),
      company: getValue(profileData.company),
      country: getValue(profileData.country),
      height: profileData.height,
      gender: profileData.gender,
      education: profileData.education,
      profile_images: (() => {
        // Ensure profile images are in the correct format for ListingView
        const images = profileData.profile_images || profileData.images || [];
        if (images.length > 0) {
          return images.map(img => ({
            original: img.url || img.s3Url || img.original || img.thumbnail,
            thumbnail: img.thumbnail || img.url || img.s3Url || img.original,
            is_profile: img.isProfile || img.is_profile || false,
            verification_status: img.verificationStatus || img.verification_status || 0
          }));
        }
        return [];
      })(),
      compatibility_score: undefined
    };
  };

  // Create mock listing data for preview (multiple profiles like ProfileCreation.jsx)
  const createMockListingData = () => {
    if (!profileData) return [];

    const transformedProfile = transformProfileDataForPreview();

    // Create additional mock profiles (same as ProfileCreation.jsx)
    const additionalProfiles = [
      {
        "_id": "65b75aad2fd15c1e725937d2",
        "phone": "",
        "country_code": "+91",
        "short_listed_by": [],
        "profile_verified": false,
        "profile_images": [
          {
            "id": "65f81da3f5fca5d782a2ef5c",
            "is_profile": true,
            "og_key": "65b75aad2fd15c1e725937d2/1710759331079.jpg",
            "original": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/66e12552c0164ed9ee4d921e/1726031326019.jpg",
            "thumb_key": "65b75aad2fd15c1e725937d2/1710759331079-thumbnail.jpg",
            "thumbnail": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/66e12552c0164ed9ee4d921e/1726031326019.jpg",
            "verification_status": 1,
            "_id": "65f81da3f5fca5d782a2ef5d"
          }
        ],
        "updated_at": "2024-04-15T05:27:47.067Z",
        "age": 18,
        "city": {
          "label": "Cheruthuruthy",
          "code": "KL020"
        },
        "country": {
          "label": "India",
          "code": "IN"
        },
        "dob": "2006-03-18T00:00:00.000Z",
        "gender": {
          "label": "Female",
          "code": "1"
        },
        "location": "Kerala Kalamandalam",
        "name": "Sneha",
        "religion": {
          "label": "Buddhist",
          "code": "BUD"
        },
        "state": {
          "label": "Kerala",
          "code": "KL"
        },
        "astro_sign": "Pisces",
        "bio": "Hello, I am Sneha. I hail from Cheruthuruthy. I am looking for a Theravada groom.",
        "caste": {
          "code": "BUD-0",
          "label": "Theravada"
        },
        "height": {
          "label": "126 cm / 4 ft 2 in",
          "code": "126"
        },
        "marital_status": {
          "code": "0",
          "label": "Single"
        },
        "job": {
          "label": "Adjudicator",
          "code": "4"
        },
        "lumi_id": "LBF105FBIN",
        "preferences": {
          "full_name": true,
          "dob": true,
          "location": true,
          "notification": true,
          "show_contact": false
        },
        "last_login": "2024-04-15T05:27:52.894Z",
        "is_connected": false,
        "isContactPurchased": false,
        "type": "profile",
        "is_shortlisted": false,
        "status": "connect",
        "phone_number_length": 10
      },
      {
        "_id": "65b7c0a52fd15c1e72593a99",
        "phone": "",
        "country_code": "+91",
        "short_listed_by": [],
        "profile_verified": false,
        "profile_images": [
          {
            "id": "65b7c0fc00fb4ce3b114a6b6",
            "is_profile": true,
            "og_key": "65b7c0a52fd15c1e72593a99/1706541308387.jpg",
            "original": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/65b7c0a52fd15c1e72593a99/1706541308387.jpg",
            "thumb_key": "65b7c0a52fd15c1e72593a99/1706541308387-thumbnail.jpg",
            "thumbnail": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/65b7c0a52fd15c1e72593a99/1706541308387-thumbnail.jpg",
            "verification_status": 1,
            "_id": "65b7c0fc00fb4ce3b114a6b7"
          }
        ],
        "updated_at": "2024-06-24T10:09:39.373Z",
        "age": 18,
        "city": {
          "label": "Kochi",
          "code": "KL016"
        },
        "country": {
          "label": "India",
          "code": "IN"
        },
        "dob": "2006-01-29T00:00:00.000Z",
        "gender": {
          "label": "Female",
          "code": "1"
        },
        "location": "Kochi",
        "name": "Aswathy",
        "religion": {
          "label": "Hindu",
          "code": "HIN"
        },
        "state": {
          "label": "Kerala",
          "code": "KL"
        },
        "astro_sign": "Aquarius",
        "bio": "Hello, I am Aswathy. I hail from Kochi. I am looking for an Adi Dravidar groom.",
        "caste": {
          "label": "Adi Dravidar",
          "code": "HIN-163"
        },
        "height": {
          "label": "129 cm / 4 ft 3 in",
          "code": "129"
        },
        "marital_status": {
          "label": "Single",
          "code": "0"
        },
        "job": {
          "label": "Adult Education Teacher",
          "code": "8"
        },
        "lumi_id": "LBF10581IN",
        "preferences": {
          "full_name": true,
          "dob": true,
          "location": true,
          "notification": true,
          "show_contact": false
        },
        "last_login": "2024-06-24T10:09:37.090Z",
        "is_connected": false,
        "isContactPurchased": false,
        "type": "profile",
        "is_shortlisted": false,
        "status": "connect",
        "phone_number_length": 10
      }
    ];

    // Return only additional profiles (exclude current profile to prevent duplication)
    // The current profile will be shown via the Advertisement component (children)
    const filteredProfiles = additionalProfiles.filter(profile => 
      profile.name !== transformedProfile.name
    );

    return filteredProfiles;
  };

  // Helper function to extract habit/lifestyle data
  const getHabitValue = (habitCode, displayFieldName) => {
    // Try to get from habit array (API format)
    if (profileData?.habit && Array.isArray(profileData.habit)) {
      const habitItem = profileData.habit.find(h => h.code === habitCode);
      if (habitItem?.sub_division?.[0]?.label) {
        return habitItem.sub_division[0].label;
      }
    }

    // Try to get from transformed display fields
    if (displayFieldName && profileData?.[displayFieldName]) {
      return profileData[displayFieldName];
    }

    // Try originalData
    if (profileData?.originalData?.habit && Array.isArray(profileData.originalData.habit)) {
      const habitItem = profileData.originalData.habit.find(h => h.code === habitCode);
      if (habitItem?.sub_division?.[0]?.label) {
        return habitItem.sub_division[0].label;
      }
    }

    return 'Not specified';
  };

  // Helper function to get languages
  const getLanguages = () => {
    // Check languages_known array (API format)
    const languagesKnown = profileData?.languages_known || profileData?.knownLanguages || [];
    if (Array.isArray(languagesKnown) && languagesKnown.length > 0) {
      const validLanguages = languagesKnown.filter(lang =>
        lang && (lang.label || (typeof lang === 'string' && lang.trim() !== ''))
      );
      if (validLanguages.length > 0) {
        return validLanguages.map(lang =>
          typeof lang === 'string' ? lang : (lang.label || lang)
        ).join(', ');
      }
    }
    return 'Not specified';
  };

  // Helper function to format birth time
  const getBirthTime = () => {
    // Check multiple possible field names
    const birthTime = profileData?.birthTime ||
      profileData?.birth_time ||
      profileData?.birth_date_time ||
      profileData?.originalData?.birth_time ||
      profileData?.originalData?.birth_date_time;

    // Check if it's a valid date (not default zero value)
    if (!birthTime || birthTime === "0001-01-01T00:00:00Z" || birthTime === "0001-01-01T00:00:00.000Z") {
      return 'Not specified';
    }

    // If it's already formatted as a string (like "09:30 AM"), return it
    if (typeof birthTime === 'string' && !birthTime.includes('T') && !birthTime.includes('Z')) {
      return birthTime;
    }

    // Try to format ISO date string to time (without timezone conversion)
    try {
      // Extract time directly from ISO string to avoid timezone issues
      // Format: 2025-10-08T18:45:00Z or 2025-10-08T18:45:00.000Z
      const timeMatch = birthTime.match(/T(\d{2}):(\d{2})/);

      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);

        // Format time as HH:MM AM/PM
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');

        return `${displayHours}:${displayMinutes} ${period}`;
      }

      // Fallback to Date parsing if regex doesn't match
      const date = new Date(birthTime);
      if (isNaN(date.getTime())) {
        return 'Not specified';
      }

      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');

      return `${displayHours}:${displayMinutes} ${period}`;
    } catch (e) {
      return renderValue(birthTime);
    }
  };

  // Show loading spinner if loading is true
  if (loading) {
    return (
      <div className="profile_details_cards">
        <div className="text-center p-4">
          <Spin size="large" />
          <p style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
            Loading profile details...
          </p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile_details_cards">
        <div className="text-center p-4">
          <p>No profile data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile_details_cards">
      {/* Preview Button */}
      <div className="profile_wrap">
        <div className="top_wrap show_preview preview_btn profile_details_preview">
          <button className='sticky_button mobile_preview' onClick={showPreviewModal}>
            <Tooltip title="Mobile Preview" placement="top">
              <img src={previewIcon} alt="Preview" />
            </Tooltip>
          </button>
        </div>
      </div>

      <div className="profile_info_cards">
        {/* Job & Education */}
        <Card className="info_card job_education_card">
          <div className="card_header">
            <Tag className="text_sm">Job & Education</Tag>
          </div>
          <div className="card_content">
            <div className="info_row">
              <div className="info_item">
                <span className="label"><strong>Job</strong></span>
                <span className="value">
                  <span>{getFieldValueWithFallbacks(['job.label', 'job', 'originalData.job.label', 'originalData.job'])}</span>
                  {/* <span>({getFieldValueWithFallbacks(['city.city.label', 'city.display_place', 'location', 'originalData.city.city.label', 'originalData.city.display_place'], 'Location not specified')})</span> */}
                </span>
              </div>
              <div className="info_item">
                <span className="label"><strong>Education</strong></span>
                <span className="value">
                  <span>{getFieldValueWithFallbacks(['education.label', 'education', 'originalData.education.label', 'originalData.education'])}</span>
                  {/* <span>({getFieldValueWithFallbacks(['city.city.label', 'city.display_place', 'location', 'originalData.city.city.label', 'originalData.city.display_place'], 'Location not specified')})</span> */}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Horoscope */}
        <Card className="info_card horoscope_card">
          <div className="card_header">
            <Tag className="text_sm">Horoscope</Tag>
          </div>
          <div className="card_content">
            <div className="info_item birth_star">
              <span className="label label_min_width">Birth Star :&nbsp;&nbsp;&nbsp;{getFieldValueWithFallbacks(['birth_star.label', 'birth_star', 'birthStar', 'originalData.birth_star.label', 'originalData.birth_star'])}</span>
            </div>
            <div className="info_item birth_time">
              <span className="label label_min_width">Birth Time :&nbsp;&nbsp;&nbsp;{getBirthTime()}</span>
            </div>
            <div className="info_item birth_place">
              <span className="label label_min_width">Birth Place :&nbsp;&nbsp;&nbsp;{(() => {
                // Try to get from birth_location object (structured format)
                const birthLocation = profileData?.birthPlace || profileData?.birth_place;
                if (birthLocation ) {
                  return (
                   
                    birthLocation?.display_place ||
                    birthLocation?.display_name ||
                    birthLocation?.city?.label ||
                    birthLocation||
                    'Not specified'
                  );
                }
                return 'Not specified';
                // Fallback to simple string birth_place field
                return getFieldValueWithFallbacks(['birth_place', 'birthPlace', 'originalData.birth_place']);
              })()}</span>
            </div>
            <div className="info_item dosham">
              <span className="label label_min_width">Dosham/Manglik:&nbsp;&nbsp;&nbsp;{(() => {
                // Try multiple formats for manglik data
                const manglikData = profileData?.manglik || profileData?.dosham;
                if (manglikData && typeof manglikData === 'object' && manglikData.label) {
                  return manglikData.label;
                }
                if (typeof manglikData === 'string' && manglikData.trim() !== '') {
                  return manglikData;
                }
                // Fallback to other possible locations
                const manglikFallback = getFieldValueWithFallbacks(['originalData.manglik.label', 'originalData.manglik', 'originalData.dosham.label', 'originalData.dosham']);
                return manglikFallback && manglikFallback.trim() !== '' ? manglikFallback : 'Not specified';
              })()}</span>
            </div>
            {/* Horoscope Images */}
            {(() => {
              const horoscopeImages = profileData?.horoscope_images ||
                profileData?.horoscopeImages ||
                profileData?.originalData?.horoscope_images || [];
              return horoscopeImages.length > 0 && (
                <div className="horoscope_images">
                  <div className="horoscope_images_grid">
                    {horoscopeImages.map((image, index) => (
                      <div key={index} className="horoscope_image_item">
                        <img
                          src={image?.original || image?.thumbnail || image?.url}
                          alt={`Horoscope ${index + 1}`}
                          className="horoscope_image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </Card>

        {/* Interests */}
        <Card className="info_card interests_card">
          <div className="card_header">
            <Tag className="text_sm">Interest</Tag>
          </div>
          <div className="card_content">
            <div className="interests_grid">
              {(() => {
                const interests = profileData?.interests || [];
                const validInterests = interests.filter(interest =>
                  interest && interest.label && interest.label.trim() !== ''
                );
                return validInterests.length > 0 ? (
                  validInterests.map((interest, index) => (
                    <Tag key={index} className="interest_tag text_sm">
                      {typeof interest === 'string' ? interest : (interest?.label || interest)}
                    </Tag>
                  ))
                ) : (
                  <span className="no_data">No interests specified</span>
                );
              })()}
            </div>
          </div>
        </Card>
        {/* Family Details */}
        <Card className="info_card family_details_card">
          <div className="card_header">
            <Tag className="text_sm">Family Details</Tag>
          </div>
          <div className="card_content">
            <div className="info_item">
              <span className="label label_min_width">Family Name</span>
              <span className="value">:&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.family_name, profileData?.familyName)}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Family Status:&nbsp;&nbsp;&nbsp;{(() => {
                // Try to get family type/status from multiple sources
                const familyType = profileData?.family?.family_type;
                if (familyType && typeof familyType === 'object' && familyType.label) {
                  return familyType.label;
                }
                return getFieldValue(
                  profileData?.family?.family_status || profileData?.family_status,
                  profileData?.familyStatus
                );
              })()}</span>
            </div>
            <div className="info_item width_full">
              <span className="label label_min_width ">Family Location:&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.family_location, profileData?.familyLocation)}</span>
            </div>
            <div className="family_details_grid">
              <div className="family_details_grid_inner">
                <div className="info_item">
                  <span className="label label_min_width">Father:&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.father_name, profileData?.fatherName)}</span>
                </div>
                <div className="info_item">
                  <span className="label label_min_width">Occupation (Father):&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.father_job, profileData?.fatherOccupation)}</span>
                </div>
                <div className="info_item">
                  <span className="label label_min_width">Mother:&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.mother_name, profileData?.motherName)}</span>
                </div>
                <div className="info_item">
                  <span className="label label_min_width">Occupation (Mother):&nbsp;&nbsp;&nbsp;{getFieldValue(profileData?.family?.mother_job, profileData?.motherOccupation)}</span>
                </div>
              </div>
              <div className="siblings_display_box">
                <div className="siblings_selector">
                  <label className="input_label text_red">Siblings</label>
                  <div className="siblings_count_inline">
                    <div className="sibling_count_item">
                      <span className="sibling_label"><span>Brother(s)</span><span>:</span></span>
                      <div className="siblings_count_select">
                        <span>{brothers}</span>
                      </div>
                    </div>
                    <div className="sibling_count_item">
                      <span className="sibling_label"><span>Sister(s)</span><span>:</span></span>
                      <div className="siblings_count_select">
                        <span>{sisters}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {(() => {
              const noOfChildren = profileData?.family?.no_of_children ??
                profileData?.no_of_children ??
                profileData?.noOfChildren;
              return (noOfChildren !== undefined && noOfChildren !== null) && (
                <div className="info_item width_full">
                  <span className="label label_min_width">Number of Children</span>
                  <span className="value">:&nbsp;&nbsp;&nbsp;{noOfChildren}</span>
                </div>
              );
            })()}
            {(profileData?.family?.family_bio || profileData?.familyDescription) && (
              <div className="info_item width_full">
                <span className="label label_min_width">Family Description</span>
                <span className="value">:&nbsp;&nbsp;&nbsp;{profileData?.family?.family_bio || profileData?.familyDescription}</span>
              </div>
            )}
            {/* Siblings Display Box */}

          </div>
        </Card>



        {/* Languages */}
        <Card className="info_card languages_card">
          <div className="card_header">
            <Tag className="text_sm">Languages</Tag>
          </div>
          <div className="card_content">
            <div className="info_item">
              <span className="label label_min_width">Mother Tongue:&nbsp;&nbsp;&nbsp;{getFieldValueWithFallbacks(['mother_tongue.label', 'mother_tongue', 'motherTongue', 'originalData.mother_tongue.label', 'originalData.mother_tongue'])}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Known Languages:&nbsp;&nbsp;&nbsp;{getLanguages()}</span>
            </div>
          </div>
        </Card>
        {/* Physical Attributes */}
        <Card className="info_card physical_attributes_card">
          <div className="card_header">
            <Tag className="text_sm">Physical Attributes</Tag>
          </div>
          <div className="card_content">
            <div className="info_item">
              <span className="label label_min_width">Physical Disability:&nbsp;&nbsp;&nbsp;{(() => {
                const isDisabled = profileData?.physically_disabled ??
                  profileData?.isDisabled ??
                  profileData?.isDisabled ??
                  profileData?.originalData?.physically_disabled;
                // Handle various truthy/falsy representations
                if (isDisabled === true || isDisabled === 'true' || isDisabled === 1 || isDisabled === '1' || isDisabled === 'Yes') {
                  return 'Yes';
                }
                if (isDisabled === false || isDisabled === 'false' || isDisabled === 0 || isDisabled === '0' || isDisabled === 'No') {
                  return 'No';
                }
                return 'Not specified';
              })()}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Height:&nbsp;&nbsp;&nbsp;{formatHeightDisplay(
                profileData?.height?.label,
                profileData?.height,
                profileData?.originalData?.height?.label,
                profileData?.originalData?.height
              )}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Weight:&nbsp;&nbsp;&nbsp;{getFieldValueWithFallbacks([
                'weight.label', 'weight', 'originalData.weight.label', 'originalData.weight'
              ])}</span>
            </div>
          </div>
        </Card>

        {/* Ideologies */}
        <Card className="info_card ideologies_card">
          <div className="card_header">
            <Tag className="text_sm">Lifestyle</Tag>
          </div>
          <div className="card_content">
            <div className="info_item">
              <span className="label label_min_width">Food:&nbsp;&nbsp;&nbsp;{getHabitValue("0", "food")}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Drinking:&nbsp;&nbsp;&nbsp;{getHabitValue("1", "drinking")}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Smoking:&nbsp;&nbsp;&nbsp;{getHabitValue("2", "smoking")}</span>
            </div>
            <div className="info_item">
              <span className="label label_min_width">Ideologies & Beliefs:&nbsp;&nbsp;&nbsp;{getHabitValue("3", "ideologies")}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Preview Modal */}
      <Modal
        className="sm_modal new_add_modal work_modal tex-center mobile_preview"
        title={
          <div className="modal_header">
            <h4>Profile Preview</h4>
            <Button
              type="text"
              onClick={handlePreviewCancel}
              className="custom-close-button"
            >
              Close
            </Button>
          </div>
        }
        open={isPreviewModalOpen}
        onCancel={handlePreviewCancel}
        footer={null}
        closeIcon={null}
      >
        {/* Tab Navigation */}
        <div className="preview-tabs">
          <button
            className={`tab-button ${activePreviewTab === 'listing' ? 'active' : ''}`}
            onClick={() => setActivePreviewTab('listing')}
          >
            Listing
          </button>
          <button
            className={`tab-button ${activePreviewTab === 'profile-details' ? 'active' : ''}`}
            onClick={() => setActivePreviewTab('profile-details')}
          >
            Profile Details
          </button>
        </div>

        {/* Tab Content */}
        <div className="preview-content">
          {activePreviewTab === 'listing' ? (
            <Mobile action="enquiry" previewTab={'listing'}>
              <ListingView data={createMockListingData()}>
                <Advertisement
                  action="enquiry"
                  poster={(() => {
                    // Get the first profile image URL
                    const images = profileData?.images || profileData?.profile_images || [];
                    if (images.length > 0) {
                      return images[0]?.url || images[0]?.s3Url || images[0]?.original || '';
                    }
                    return '';
                  })()}
                  icon="enquiry"
                  profileData={transformProfileDataForPreview()}
                />
              </ListingView>
            </Mobile>
          ) : (
            <Mobile action="enquiry" previewTab={'profile-details'}>
              <ProfileDetails
                className={'right_aligned'}
                profileData={transformProfileDataForPreview()}
              />
            </Mobile>
          )}
        </div>
      </Modal>
    </div>
  );
};

ProfileDetailsCards.propTypes = {
  profileData: PropTypes.object,
  loading: PropTypes.bool
};

ProfileDetailsCards.defaultProps = {
  profileData: null,
  loading: false
};

export default ProfileDetailsCards;